import {
	INIT,
	MEXICO_CITY,
	MEXICO_CITY_ACCENT,
	OTHER_COLONY,
	US_COUNTRY_ID,
} from '@/components/blocks/AddressForm/constants';
import StatesCitiesList from '@/components/blocks/AddressForm/data/FormStatesCitiesList.json';
import { AddressErrors, JsonStates } from '@/components/blocks/AddressForm/types';
import { capitalizeFirst } from '@/components/blocks/AddressForm/utils';
import { DEFAULT_COUNTRY_ID, geocodeByZipCode } from '@/data/Content/GeocodeByZipCode';
import { useLocationByZipCode } from '@/data/Content/HdmLocationByZipCode';
import { useLocalization } from '@/data/Localization';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { Address, EditableAddress } from '@/data/types/Address';
import { ADDRESS_BILLING, ADDRESS_SHIPPING_BILLING } from '@/utils/address';
import { FormState } from '@/utils/useForm';
import { SelectChangeEvent } from '@mui/material';
import { LocationFetchResult } from 'integration/generated/custom/data-contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';

type UseAddressFormFieldsProps = {
	form: FormState<EditableAddress>;
	formLabel?: JSX.Element;
	isTwoColumns: boolean;
	displayAddresses?: Address[];
	hiddenNicknameField: boolean;
	isCheckout: boolean;
	isNotBilling: boolean;
	addressErrors: AddressErrors;
	setAddressErrors: React.Dispatch<React.SetStateAction<AddressErrors>>;
	setIsEdited: React.Dispatch<React.SetStateAction<boolean>>;
	showInputStateAndCity: boolean;
	setShowInputStateAndCity: React.Dispatch<React.SetStateAction<boolean>>;
	addressType?: 'Billing';
	isMainAddress?: boolean;
};

export const useAddressFormFields = ({
	form: {
		values,
		handleAutoCompleteInputChange,
		handleInputChange,
		handleSelectChange,
		onNamedValueChange,
		error,
	},
	formLabel,
	isTwoColumns,
	displayAddresses,
	isCheckout,
	isNotBilling,
	addressErrors,
	setAddressErrors,
	setIsEdited,
	showInputStateAndCity,
	setShowInputStateAndCity,
	hiddenNicknameField,
	addressType,
	...props
}: UseAddressFormFieldsProps) => {
	const edit = Boolean(values.addressId);
	const nickNameDisabled = Boolean(values.addressId);
	const isMainAddress =
		props.isMainAddress !== undefined
			? props.isMainAddress
			: Boolean(values.nickName === values.email1);
	const addressFormNLS = useLocalization('AddressForm');

	const [cities, setCities] = useState<string[]>([]);
	const [colonies, setColonies] = useState<string[]>([INIT, OTHER_COLONY]);
	const [states, setStates] = useState<string[]>([]);
	const [countries, setCountries] = useState<string[]>([INIT, DEFAULT_COUNTRY_ID, US_COUNTRY_ID]);
	const [showInputCity, setShowInputCity] = useState<boolean>(false);
	const [cityDisabled, setCityDisabled] = useState<boolean>(true);
	const [stateDisabled, setStateDisabled] = useState<boolean>(true);
	const [colonyDisabled, setColonyDisabled] = useState<boolean>(true);
	const [countryDisabled, setCountryDisabled] = useState<boolean>(false);

	const zipCodeErrorMessageByFlow = isCheckout
		? addressFormNLS.Msgs.ZipError.t()
		: addressFormNLS.Msgs.InvalidZipCode.t();

	const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState<string>(
		addressFormNLS.Msgs.RequiredField.t()
	);
	const [nickNameErrorMessage, setNickNameErrorMessage] = useState<string>(
		addressFormNLS.Msgs.RequiredField.t()
	);

	const { getLocationByZipCode } = useLocationByZipCode();

	const zipCodeErrors =
		addressType === ADDRESS_BILLING || addressType === ADDRESS_SHIPPING_BILLING
			? addressFormNLS.Msgs.ZipErrorCard.t()
			: zipCodeErrorMessageByFlow;

	const helperText = useMemo(
		() => ({
			nickName: addressErrors.nickName ? nickNameErrorMessage : EMPTY_STRING,
			street: addressErrors.street ? addressFormNLS.Msgs.RequiredField.t() : EMPTY_STRING,
			exteriorNumber: addressErrors.exteriorNumber
				? addressFormNLS.Msgs.RequiredField.t()
				: EMPTY_STRING,
			zipCode: addressErrors.zipCode
				? zipCodeErrorMessage || addressFormNLS.Msgs.RequiredField.t()
				: EMPTY_STRING,
			colony: addressErrors.colony ? addressFormNLS.Msgs.RequiredField.t() : EMPTY_STRING,
			colonyNotFound: addressErrors.colonyNotFound
				? addressFormNLS.Msgs.RequiredField.t()
				: EMPTY_STRING,
			municipality: addressErrors.municipality
				? addressFormNLS.Msgs.RequiredField.t()
				: EMPTY_STRING,
			state: addressErrors.state ? addressFormNLS.Msgs.RequiredField.t() : EMPTY_STRING,
			firstName: addressErrors.firstName ? addressFormNLS.Msgs.RequiredField.t() : EMPTY_STRING,
			lastName: addressErrors.lastName ? addressFormNLS.Msgs.RequiredField.t() : EMPTY_STRING,
		}),
		[addressFormNLS, addressErrors, zipCodeErrorMessage, nickNameErrorMessage]
	);

	const noGoogleMode = useCallback(() => {
		if (edit && values.addressLine3 && values.addressLine3 !== INIT) {
			onNamedValueChange('addressLine3Txt', values.addressLine3);
			if (!isNotBilling) {
				onNamedValueChange('addressLine3', OTHER_COLONY);
			}
		} else {
			if (!isNotBilling) {
				onNamedValueChange('addressLine3', OTHER_COLONY);
			}
			onNamedValueChange('addressLine3Txt', EMPTY_STRING);
			onNamedValueChange('city', EMPTY_STRING);
			onNamedValueChange('state', EMPTY_STRING);
		}
		setColonies([INIT, OTHER_COLONY]);
		setStates([INIT]);
		setCountries([INIT, DEFAULT_COUNTRY_ID, US_COUNTRY_ID]);
		setCountryDisabled(true);
		setColonyDisabled(true);
		setCityDisabled(true);
		setStateDisabled(true);
		setZipCodeErrorMessage(zipCodeErrors);
		setAddressErrors((prevState) => ({
			...prevState,
			zipCode: true,
			municipality: false,
			state: false,
		}));
		if (isNotBilling) return;
		setShowInputStateAndCity(true);
	}, [
		edit,
		onNamedValueChange,
		values.addressLine3,
		setColonies,
		setStates,
		setColonyDisabled,
		setCityDisabled,
		setStateDisabled,
		setZipCodeErrorMessage,
		setAddressErrors,
		setShowInputStateAndCity,
		zipCodeErrors,
		isNotBilling,
	]);

	const cleanFieldsPaste = useCallback(() => {
		onNamedValueChange('addressLine3', EMPTY_STRING);
		onNamedValueChange('city', EMPTY_STRING);
		onNamedValueChange('state', EMPTY_STRING);
	}, [onNamedValueChange]);

	const mapLocationByZipCodeCall = useCallback(
		(results: LocationFetchResult) => {
			let addressLine3;
			if (edit) {
				addressLine3 = values.addressLine3;
			}
			setShowInputStateAndCity(false);
			setShowInputCity(false);

			if (results?.state?.name) {
				setAddressErrors((prevState) => ({
					...prevState,
					zipCode: false,
				}));
				let stateValue = capitalizeFirst(results.state.name.toLowerCase());
				const jsonStates = StatesCitiesList.formStatesObject;
				jsonStates.forEach((e: JsonStates) => {
					if (e.xstatesabbr === results.state.id) {
						stateValue = e.totstate;
					}
				});
				const jsonColonies = results.colonies;
				const jsonCities = results.city;
				const arrayColonies: string[] = [];
				jsonColonies.forEach((e) => {
					arrayColonies.push(capitalizeFirst(e.name.toLowerCase()));
				});
				const arrayCities: string[] = [];
				jsonCities.forEach((e) => {
					arrayCities.push(capitalizeFirst(e.name.toLowerCase()));
				});
				setStates([stateValue]);

				if (edit && addressLine3 && addressLine3 !== INIT) {
					const foundColony = arrayColonies.includes(addressLine3 ?? '');
					if (!foundColony) {
						onNamedValueChange('addressLine3Txt', addressLine3);
						onNamedValueChange('addressLine3', OTHER_COLONY);
					}
				} else {
					onNamedValueChange('addressLine3', addressLine3 || INIT);
					onNamedValueChange('addressLine3Txt', EMPTY_STRING);
				}

				onNamedValueChange('state', stateValue);
				setCities([INIT, ...arrayCities]);
				setColonies([INIT, ...arrayColonies, OTHER_COLONY]);
				setColonyDisabled(false);
				setStateDisabled(true);
				setCountries([INIT, DEFAULT_COUNTRY_ID, US_COUNTRY_ID]);
				onNamedValueChange('country', DEFAULT_COUNTRY_ID);
				setCountryDisabled(true);
				if (stateValue.toLowerCase() === MEXICO_CITY_ACCENT.toLowerCase()) {
					setCityDisabled(false);
					if (values.city === EMPTY_STRING) {
						onNamedValueChange('city', INIT);
					}
				} else {
					onNamedValueChange('city', arrayCities[0]);
					setCityDisabled(true);
				}
			} else {
				noGoogleMode();
			}
		},
		[
			edit,
			values.addressLine3,
			onNamedValueChange,
			setAddressErrors,
			setStates,
			setColonies,
			setCityDisabled,
			setStateDisabled,
			setColonyDisabled,
			noGoogleMode,
			setShowInputStateAndCity,
			setShowInputCity,
			values.city,
		]
	);

	const getGeoLocationByZipCode = useCallback(
		async (zipCode: string) => {
			try {
				const result = await getLocationByZipCode(zipCode);
				mapLocationByZipCodeCall(result);
			} catch (e) {
				noGoogleMode();
			}
		},
		[getLocationByZipCode, mapLocationByZipCodeCall, noGoogleMode]
	);

	const setCitiesSelect = useCallback(
		(state: string) => {
			const cities =
				StatesCitiesList.formCitiesObject?.[
					state as keyof typeof StatesCitiesList.formCitiesObject
				];
			const jsonCities = cities?.split('|') ?? [];
			setCities([INIT, ...jsonCities]);
		},
		[setCities]
	);

	const fetchStateFromZipCode = useCallback(
		async (zipCode: string) => {
			try {
				const results = await getLocationByZipCode(zipCode);
				if (results?.state?.name) {
					let stateValue = capitalizeFirst(results.state.name.toLowerCase());
					const jsonStates = StatesCitiesList.formStatesObject;
					jsonStates.forEach((e: JsonStates) => {
						if (e.xstatesabbr === results.state.id) {
							stateValue = e.totstate;
						}
					});
					setStates([stateValue]);
					onNamedValueChange('state', stateValue);
					setStateDisabled(true);
				} else {
					noGoogleMode();
				}
			} catch (e) {
				noGoogleMode();
			}
		},
		[getLocationByZipCode, onNamedValueChange, noGoogleMode]
	);

	const mapGeocodeData = useCallback(
		// eslint-disable-next-line complexity
		(results: google.maps.GeocoderResult[] | null) => {
			if (!results || results.length === 0) return;

			const stateCity = results[0].address_components;
			const postcode_localities = results[0].postcode_localities ?? [];

			const findComponent = (type: string) =>
				stateCity.find((c: google.maps.GeocoderAddressComponent) => c.types.includes(type));

			const googleCountry = findComponent('country')?.short_name ?? DEFAULT_COUNTRY_ID;
			const isMexico = googleCountry === DEFAULT_COUNTRY_ID;
			const zipCode = findComponent('postal_code')?.short_name ?? '';

			const stateComponent = stateCity.filter((e) =>
				e.types.includes('administrative_area_level_1')
			);
			const localityComponent = stateCity.filter(
				(e) => e.types.includes('locality') || e.types.includes('administrative_area_level_2')
			);
			const hasColonies = postcode_localities.length > 0;
			const hasMunicipio = localityComponent.length > 0;
			const hasGoogleState = stateComponent.length > 0;

			if (stateCity.length === 0) {
				if (values.country === DEFAULT_COUNTRY_ID) {
					getGeoLocationByZipCode(values.zipCode);
				} else {
					noGoogleMode();
				}
				return;
			}

			setShowInputStateAndCity(false);
			setShowInputCity(false);

			setCountries([googleCountry]);
			onNamedValueChange('country', googleCountry);
			setCountryDisabled(true);

			if (hasGoogleState) {
				const jsonStates = StatesCitiesList.formStatesObject;
				const stateShortName = stateComponent[0].short_name;
				let stateName = stateComponent[0].long_name;
				jsonStates.forEach((e: JsonStates) => {
					if (e.googlestateabbr === stateShortName) {
						stateName = e.totstate;
					}
				});
				setStates([stateName]);
				onNamedValueChange('state', stateName);
				setStateDisabled(true);
			} else if (isMexico) {
				void fetchStateFromZipCode(zipCode);
			} else {
				onNamedValueChange('state', INIT);
				onNamedValueChange('addressLine3', showInputStateAndCity ? OTHER_COLONY : INIT);
				setColonies([INIT, OTHER_COLONY]);
				setStates([INIT]);
				setAddressErrors((prevState) => ({ ...prevState, zipCode: true }));
				setCityDisabled(true);
				return;
			}

			if (hasColonies) {
				setColonies([INIT, ...postcode_localities, OTHER_COLONY]);
				if (!edit) {
					onNamedValueChange('addressLine3', INIT);
				} else if (values.addressLine3 && values.addressLine3 !== INIT) {
					const foundColony = postcode_localities.includes(values.addressLine3);
					if (!foundColony) {
						onNamedValueChange('addressLine3Txt', values.addressLine3);
						onNamedValueChange('addressLine3', OTHER_COLONY);
					}
				}
				setColonyDisabled(false);
			} else {
				setColonies([INIT, OTHER_COLONY]);
				onNamedValueChange('addressLine3', OTHER_COLONY);
				if (!edit) {
					onNamedValueChange('addressLine3Txt', EMPTY_STRING);
				} else if (values.addressLine3 && values.addressLine3 !== INIT) {
					onNamedValueChange('addressLine3Txt', values.addressLine3);
				}
				setColonyDisabled(false);
			}

			if (hasMunicipio) {
				const localityName = localityComponent[0].long_name;
				if (localityName === MEXICO_CITY_ACCENT || localityName === MEXICO_CITY) {
					setCitiesSelect(MEXICO_CITY);
					setCityDisabled(false);
					if (values.city === EMPTY_STRING) {
						onNamedValueChange('city', INIT);
					}
				} else {
					setCities([localityName]);
					onNamedValueChange('city', localityName);
					setCityDisabled(true);
				}
			} else {
				setShowInputCity(true);
				setCityDisabled(false);
				if (!edit || values.city === INIT) {
					onNamedValueChange('city', EMPTY_STRING);
				}
			}

			setAddressErrors((prevState) => ({ ...prevState, zipCode: false }));
		},
		[
			setShowInputStateAndCity,
			onNamedValueChange,
			setAddressErrors,
			values.country,
			values.zipCode,
			values.addressLine3,
			values.city,
			getGeoLocationByZipCode,
			noGoogleMode,
			fetchStateFromZipCode,
			showInputStateAndCity,
			edit,
			setCitiesSelect,
		]
	);

	const handleZipCodeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleInputChange(event);
		setIsEdited?.(true);
		const zipCodeValue = event.target.value;

		if (zipCodeValue.length < 5) {
			setColonyDisabled(true);
			if (zipCodeValue.length === 0) {
				setZipCodeErrorMessage(addressFormNLS.Msgs.RequiredField.t());
				setAddressErrors((prevState) => ({
					...prevState,
					zipCode: false,
				}));
			} else {
				setZipCodeErrorMessage(addressFormNLS.Msgs.InvalidZipCode.t());
				setAddressErrors((prevState) => ({
					...prevState,
					zipCode: true,
				}));
			}
			setShowInputCity(false);
			setCityDisabled(true);
			setColonies([INIT, OTHER_COLONY]);
			setCities([INIT]);
			setStates([INIT]);
			setCountries([INIT, DEFAULT_COUNTRY_ID, US_COUNTRY_ID]);
			setCountryDisabled(false);
			setAddressErrors((prevState) => ({
				...prevState,
				colony: false,
				colonyNotFound: false,
				municipality: false,
			}));
			setStateDisabled(true);
		} else {
			setColonyDisabled(false);
			getGeoData(zipCodeValue);
			if (addressErrors.zipCode && zipCodeErrorMessage !== zipCodeErrorMessageByFlow) {
				setZipCodeErrorMessage(addressFormNLS.Msgs.RequiredField.t());
				setAddressErrors((prevState) => ({
					...prevState,
					zipCode: false,
				}));
			}
		}

		onNamedValueChange('addressLine3', isNotBilling ? INIT : EMPTY_STRING);
		onNamedValueChange('city', isNotBilling ? INIT : EMPTY_STRING);
		onNamedValueChange('state', isNotBilling ? INIT : EMPTY_STRING);
		onNamedValueChange('addressLine3Txt', EMPTY_STRING);
		if (!values.country) {
			onNamedValueChange('country', DEFAULT_COUNTRY_ID);
		}
	};

	const validateNickName = (nickName: string) => {
		const nickNameExists = displayAddresses?.some(
			(address: Address) => address.nickName.trim().toLowerCase() === nickName.trim().toLowerCase()
		);
		if (nickNameExists) {
			setNickNameErrorMessage(addressFormNLS.Msgs.InvalidNickName.t());
			setAddressErrors((prevState) => ({
				...prevState,
				nickName: true,
			}));
		} else {
			setNickNameErrorMessage(addressFormNLS.Msgs.RequiredField.t());
			setAddressErrors((prevState) => ({
				...prevState,
				nickName: nickName.trim() === EMPTY_STRING,
			}));
		}
	};

	const handleColonySelectChange = (event: SelectChangeEvent) => {
		handleSelectChange(event);
		if (event.target.value !== INIT && addressErrors.colony) {
			setAddressErrors((prevState) => ({
				...prevState,
				colony: false,
			}));
		}

		if (event.target.value !== OTHER_COLONY) {
			onNamedValueChange('addressLine3Txt', EMPTY_STRING);
		}
	};

	const handleMunicipalitySelectChange = (event: SelectChangeEvent) => {
		handleSelectChange(event);
		if (event.target.value !== INIT && addressErrors.municipality) {
			setAddressErrors((prevState) => ({
				...prevState,
				municipality: false,
			}));
		}
	};

	const getGeoData = useCallback(
		async (zipCode: string) => {
			try {
				const countryRestriction = values.country || DEFAULT_COUNTRY_ID;
				const { results, status } = await geocodeByZipCode(zipCode, undefined, countryRestriction);

				if (status === google.maps.GeocoderStatus.OK) {
					mapGeocodeData(results);
				} else if (values.country === DEFAULT_COUNTRY_ID || !values.country) {
					await getGeoLocationByZipCode(zipCode);
				} else {
					noGoogleMode();
				}
			} catch (e) {
				noGoogleMode();
			}
		},
		[values.country, mapGeocodeData, getGeoLocationByZipCode, noGoogleMode]
	);

	useEffect(() => {
		if (!edit) return;
		(async () => {
			if (values.zipCode.length === 5) {
				await getGeoData(values.zipCode);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.zipCode, edit]);

	return {
		address: values,
		handleAutoCompleteInputChange,
		handleInputChange,
		handleSelectChange,
		handleColonySelectChange,
		handleMunicipalitySelectChange,
		onNamedValueChange,
		handleZipCodeInputChange,
		validateNickName,
		addressErrors,
		setAddressErrors,
		nickNameDisabled,
		setIsEdited,
		error,
		cities,
		colonies,
		states,
		helperText,
		showInputCity,
		showInputStateAndCity,
		formLabel,
		isTwoColumns,
		cityDisabled,
		colonyDisabled,
		stateDisabled,
		countries,
		countryDisabled,
		isMainAddress,
		hiddenNicknameField,
		addressType,
		isNotBilling,
		cleanFieldsPaste,
	};
};
